import { useState } from 'react'
import { Link, VisuallyHidden } from '@overdose/components'
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react'
import classNames from 'classnames'
import dynamic from 'next/dynamic'
import type { Swiper } from 'swiper'
import { Button } from '~/components'
import { SectionContainer } from '~/components/SectionContainer'
import { NEW_TAB_LINK_TARGET } from '~/constants/links'
import { HeroBannerProps } from '~/sections/HeroBanner'
import { FeatureHeroBanner } from '~/sections/HeroBanner/FeatureHeroBanner'
import { HeroBannerTextContent } from '~/sections/HeroBanner/HeroBannerTextContent'
import styles from './HeroBanner.module.css'

let sectionProps: HeroBannerProps
const HeroBannerCarousel = dynamic(
  async () => {
    const mod = await import('./HeroBannerCarousel')
    return mod.HeroBannerCarousel
  },
  {
    ssr: false,
    loading: () => {
      if (!sectionProps?.slides?.[0]) {
        return null
      }

      return (
        <FeatureHeroBanner {...sectionProps?.slides?.[0]}>
          <HeroBannerTextContent
            paragraph={sectionProps?.slides?.[0]?.paragraph}
            price={sectionProps?.slides?.[0]?.price}
            title={sectionProps?.slides?.[0]?.title}
          />
        </FeatureHeroBanner>
      )
    },
  }
)

const HeroBanner = ({ width = 'full', ...props }: HeroBannerProps) => {
  sectionProps = props
  const [swiper, setSwiper] = useState<Swiper | null>(null)
  const bannerLinkUrl = props?.action?.isDisabled ? null : props.action?.href
  const bannerLinkTarget = props?.action?.openInNewTab
    ? NEW_TAB_LINK_TARGET
    : null

  const handleSlidePrev = () => {
    if (swiper) {
      swiper.slidePrev()
    }
  }

  const handleSlideNext = () => {
    if (swiper) {
      swiper.slideNext()
    }
  }

  return (
    <SectionContainer
      width={width}
      innerClassName='p-0 relative'
      wrapperId={props.uid}
      wrapperClassName={styles.sectionWrapper}>
      {props?.slides?.length > 0 ? (
        <div className={classNames('relative', styles.container)}>
          <HeroBannerCarousel setSwiper={setSwiper} slides={props.slides} />
          <div
            className='flex gap-12 absolute z-10 bottom-7 justify-center w-full'
            style={{ gap: `calc(16px * ${props.slides.length})` }}>
            <Button
              iconOnly
              variant='ghost'
              style={{
                '--btn-icon-size': '20px',
                '--btn-ghost-color-text-primary': '#FFFFFF',
              }}
              theme={{
                root: 'z-20',
              }}
              size='xs'
              aria-label='previous'
              onClick={handleSlidePrev}
              icon={<IconChevronLeft size={20} />}>
              <VisuallyHidden>Previous</VisuallyHidden>
            </Button>
            <Button
              iconOnly
              variant='ghost'
              style={{
                '--btn-icon-size': '20px',
                '--btn-ghost-color-text-primary': 'white',
              }}
              theme={{
                root: 'z-20',
              }}
              size='xs'
              aria-label='next'
              onClick={handleSlideNext}
              icon={<IconChevronRight size={20} />}>
              <VisuallyHidden>Next</VisuallyHidden>
            </Button>
          </div>
        </div>
      ) : (
        <FeatureHeroBanner {...props}>
          <HeroBannerTextContent
            paragraph={props.paragraph}
            price={props.price}
            title={props.title}
            label={props?.label}
            priceText={props?.priceText}
            saveMessage={props?.saveMessage}
          />
        </FeatureHeroBanner>
      )}
      {bannerLinkUrl && (
        <Link
          className='absolute z-10 inset-0'
          to={bannerLinkUrl}
          target={bannerLinkTarget}>
          <VisuallyHidden>{props?.action?.accessibleTitle}</VisuallyHidden>
        </Link>
      )}
    </SectionContainer>
  )
}

export { HeroBanner }
